import { useParams, useSearchParams } from "react-router";
import { useRiskAreasContext } from "@common/providers/riskAreasProvider/RiskAreasProvider";
import { RiskGovernanceFilter } from "../RiskGovernanceOverview.types";
import { buildOverviewRequest, useRiskGovernanceData } from "../RiskGovernanceOverview.hooks";

// The governance view state carried in the PDF route's `state` query param, so the printed overview reproduces
// exactly what the user was looking at (their filters, the mode and any point-in-time snapshot).
export interface RiskGovernancePdfState {
    filter: RiskGovernanceFilter;
    searchText: string;
    afterMitigation: boolean;
    asOf: string | null;
}

const emptyState: RiskGovernancePdfState = { filter: {}, searchText: "", afterMitigation: false, asOf: null };

function parseState(raw: string | null): RiskGovernancePdfState {
    if (!raw) return emptyState;
    return { ...emptyState, ...JSON.parse(raw) };
}

export function useRiskGovernanceOverviewPdf() {
    const { riskAreaId } = useParams<{ riskAreaId: string }>();
    const [searchParams] = useSearchParams();
    const { riskAreas } = useRiskAreasContext();

    const state = parseState(searchParams.get("state"));
    const request = buildOverviewRequest(riskAreaId ?? "", state.filter, state.searchText);
    // The printed overview has no cell drill-down, so the raw entities and row mapper stay behind.
    const { data, entities, toRow, responsibleUsers, ...overview } = useRiskGovernanceData(riskAreaId, request, state.afterMitigation, state.asOf);

    const riskAreaName = riskAreas.find((area) => area.id === riskAreaId)?.name ?? "";

    return { ...overview, riskAreaName, asOf: state.asOf, afterMitigation: state.afterMitigation };
}
